import React, { useEffect, useState } from 'react';
import story from '../story.json';

/**
 * DynamicImage component to handle dynamic image imports based on the provided path.
 */
const DynamicImage = ({ src, alt, className }) => {
  try {
    const imageSrc = require(`../images/${src}`);
    return (
      <img
        src={imageSrc}
        alt={alt}
        className={`max-w-[50%] max-h-[50vh] object-contain mx-auto ${className}`}
      />
    );
  } catch (error) {
    console.error(`Error loading image: ${src}`, error);
    return null;
  }
};

/**
 * This renders an item in the table of contents list.
 * scrollIntoView is used to ensure that when a user clicks on an item, it will smoothly scroll.
 */
const Headings = ({ headings, activeId, onLinkClick }) => {
  const handleLinkClick = (event, id) => {
    event.preventDefault();
    const element = document.getElementById(id);
    const offset = 85; // Adjust this value based on your navbar height
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    // Hide the TOC after clicking a heading on smaller screens
    if (window.innerWidth < 768) {
      onLinkClick();
    }
  };

  return (
    <ul className="list-none pl-1">
      {headings.map((heading) => (
        <li key={heading.id} className={heading.id === activeId ? "active" : ""}>
          <a
            href={`#${heading.id}`}
            onClick={(e) => handleLinkClick(e, heading.id)}
            className="hover:text-blue-300"
          >
            {heading.title}
          </a>
          {heading.items.length > 0 && (
            <ul className="list-none pl-4">
              {heading.items.map((child) => (
                <li
                  key={child.id}
                  className={child.id === activeId ? "active" : ""}
                >
                  <a
                    href={`#${child.id}`}
                    onClick={(e) => handleLinkClick(e, child.id)}
                    className="hover:text-blue-300"
                  >
                    {child.title}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
};

/**
 * Dynamically generates the table of contents list, using any H2s and H3s it can find in the main text
 */
const useHeadingsData = () => {
  const [nestedHeadings, setNestedHeadings] = useState([]);

  useEffect(() => {
    const headingElements = Array.from(
      document.querySelectorAll("main h2, main h3")
    );

    // Created a list of headings, with H3s nested
    const newNestedHeadings = getNestedHeadings(headingElements);
    setNestedHeadings(newNestedHeadings);
  }, []);

  return { nestedHeadings };
};

const getNestedHeadings = (headingElements) => {
  const nestedHeadings = [];

  headingElements.forEach((heading) => {
    const { innerText: title, id } = heading;

    if (heading.nodeName === "H2") {
      nestedHeadings.push({ id, title, items: [] });
    } else if (heading.nodeName === "H3" && nestedHeadings.length > 0) {
      nestedHeadings[nestedHeadings.length - 1].items.push({
        id,
        title
      });
    }
  });

  return nestedHeadings;
};

const useIntersectionObserver = (setActiveId, activeId) => {
  const headingElementsRef = React.useRef({});
  useEffect(() => {
    const callback = (headings) => {
      headingElementsRef.current = headings.reduce((map, headingElement) => {
        map[headingElement.target.id] = headingElement;
        return map;
      }, headingElementsRef.current);

      // Get all headings that are currently visible on the page
      const visibleHeadings = [];
      Object.keys(headingElementsRef.current).forEach((key) => {
        const headingElement = headingElementsRef.current[key];
        if (headingElement.isIntersecting) visibleHeadings.push(headingElement);
      });

      const getIndexFromId = (id) =>
        headingElements.findIndex((heading) => heading.id === id);

      // If there is only one visible heading, this is our "active" heading
      if (visibleHeadings.length === 1) {
        setActiveId(visibleHeadings[0].target.id);
        // If there is more than one visible heading,
        // choose the one that is closest to the top of the page
      } else if (visibleHeadings.length > 1) {
        const sortedVisibleHeadings = visibleHeadings.sort(
          (a, b) => getIndexFromId(a.target.id) > getIndexFromId(b.target.id)
        );

        setActiveId(sortedVisibleHeadings[0].target.id);
      }

      // If there are no visible headings, and we are scrolling back up, we want to make sure
      // the correct header is highlighted.
      if (visibleHeadings.length === 0) {
        const activeElement = headingElements.find((el) => el.id === activeId);
        const activeIndex = headingElements.findIndex(
          (el) => el.id === activeId
        );

        const activeIdYcoord = activeElement?.getBoundingClientRect().y;
        if (activeIdYcoord && activeIdYcoord > 150 && activeIndex !== 0) {
          setActiveId(headingElements[activeIndex - 1].id);
        }
      }
    };

    const observer = new IntersectionObserver(callback, {
      rootMargin: "0px 0px -40% 0px"
    });

    const headingElements = Array.from(document.querySelectorAll("h2, h3"));

    headingElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [setActiveId, activeId]);
};

/**
 * Renders the table of contents.
 */
const TableOfContents = ({ isVisible, onLinkClick }) => {
  const [activeId, setActiveId] = useState();
  const { nestedHeadings } = useHeadingsData();
  useIntersectionObserver(setActiveId, activeId);

  return (
    <div className={`fixed inset-y-0 right-0 w-full bg-black bg-opacity-80 text-white z-50 p-4 transition-transform transform ${isVisible ? 'translate-x-0' : 'translate-x-full'} md:relative md:translate-x-0 md:bg-transparent md:p-0`}>
      <h2 className="text-xl text-center font-bold mb-4 md:hidden">Tartalomjegyzék</h2>
      <nav className="px-1 text-center md:text-left" aria-label="Table of contents">
        <Headings headings={nestedHeadings} activeId={activeId} onLinkClick={onLinkClick} />
      </nav>
    </div>
  );
};

const Story = () => {
  const [isTocVisible, setIsTocVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleTocVisibility = () => {
    setIsTocVisible(!isTocVisible);
  };

  const hideToc = () => {
    setIsTocVisible(false);
  };

  return (
    <div className="container mx-auto p-0 md:pl-6 md:pr-0">
      <main className="max-w-3xl mx-auto p-6 relative">
        <h1 className="text-4xl font-bold text-center mb-8 text-white">Leukémia testközelből</h1>
        {story.map((section, index) => (
          <div key={index} className="mb-6">
            {section.title && (
              <h2 id={`section-${index}`} className="text-2xl font-semibold text-white">
                {section.title}
              </h2>
            )}
            {section.content.map((item, idx) => {
              if (item.type === "text") {
                return (
                  <p key={idx} className="text-lg text-justify text-gray-300 mt-2">
                    {item.value}
                  </p>
                );
              } else if (item.type === "image") {
                return (
                  <div key={idx} className="text-center my-4">
                    <DynamicImage src={item.url} alt={item.title} className="mx-auto" />
                    {item.title && <p className="text-white mt-2">{item.title}</p>}
                  </div>
                );
              } else if (item.type === "list") {
                return (
                  <ul key={idx} className="list-disc list-inside text-gray-300 mt-2 pl-6">
                    {item.value.map((listItem, listIdx) => {
                      if (Array.isArray(listItem)) {
                        // Ha a listaelem egy tömb, akkor belső listát generálunk
                        return (
                          <ul key={listIdx} className="list-disc list-inside pl-6">
                            {listItem.map((subItem, subIdx) => (
                              <li key={subIdx} className="text-lg">
                                {subItem}
                              </li>
                            ))}
                          </ul>
                        );
                      } else {
                        // Ha a listaelem string, akkor normál listaelem
                        return (
                          <li key={listIdx} className="text-lg">
                            {listItem}
                          </li>
                        );
                      }
                    })}
                  </ul>
                );
              }
              return null;
            })}
          </div>
        ))}
        {/* Statikus dátum mobil nézetben bal oldalon, nagyobb kijelzőn jobb oldalon */}
        <div className="absolute bottom-0 left-2 md:left-auto md:right-2 text-gray-200 text-sm">
          2025. május 9.
        </div>
      </main>
      <TableOfContents isVisible={isTocVisible} onLinkClick={hideToc} />
      <div className="md:hidden fixed bottom-2 right-0 z-50">
        <button
          onClick={toggleTocVisibility}
          className="bg-black text-white p-1 rounded-l-full shadow-lg focus:outline-none"
        >
          {isTocVisible ? 'Bezárás' : 'Tartalomjegyzék'}
        </button>
      </div>
    </div>
  );
};

export default Story;